// V2 Analytics API Request Factory

const BASE_V2_URL = "analytics/v2/cards";

// Helper function to create base V2 request structure
const createV2BaseRequest = (linkedin_account_id, cards_request, page_id = null) => {
  const data = {
    linkedin_account_id,
    cards_request,
  };

  // Add linked_in_company_page_id if provided (for company pages)
  if (page_id) {
    data.linked_in_company_page_id = page_id;
  }

  return {
    url: BASE_V2_URL,
    method: "POST",
    data,
  };
};

// Helper function to create card request object
const createCardRequest = (type, start_date, end_date) => {
  return {
    type,
    params: {
      start_date,
      end_date,
    },
  };
};

// Single card requests
export const createGetFollowersTrendV2Request = (linkedin_account_id, date_range, page_id = null) => {
  const cards_request = [createCardRequest("followers_trend", date_range.start_date, date_range.end_date)];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

export const createGetImpressionsTrendV2Request = (linkedin_account_id, date_range, page_id = null) => {
  const cards_request = [createCardRequest("impressions_trend", date_range.start_date, date_range.end_date)];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

export const createGetReactionsTrendV2Request = (linkedin_account_id, date_range, page_id = null) => {
  const cards_request = [createCardRequest("reactions_trend", date_range.start_date, date_range.end_date)];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

export const createGetCommentsTrendV2Request = (linkedin_account_id, date_range, page_id = null) => {
  const cards_request = [createCardRequest("comments_trend", date_range.start_date, date_range.end_date)];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

export const createGetMembersReachedTrendV2Request = (linkedin_account_id, date_range, page_id = null) => {
  const cards_request = [createCardRequest("members_reached_trend", date_range.start_date, date_range.end_date)];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

export const createGetResharesTrendV2Request = (linkedin_account_id, date_range, page_id = null) => {
  const cards_request = [createCardRequest("reshares_trend", date_range.start_date, date_range.end_date)];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

// Multiple card requests for dashboard/overview
export const createGetProfileOverviewV2Request = (linkedin_account_id, date_range, page_id = null) => {
  const cards_request = [
    createCardRequest("followers_trend", date_range.start_date, date_range.end_date),
    createCardRequest("impressions_trend", date_range.start_date, date_range.end_date),
    createCardRequest("reactions_trend", date_range.start_date, date_range.end_date),
    createCardRequest("comments_trend", date_range.start_date, date_range.end_date)
  ];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

export const createGetEngagementMetricsV2Request = (linkedin_account_id, date_range, page_id = null) => {
  const cards_request = [
    createCardRequest("impressions_trend", date_range.start_date, date_range.end_date),
    createCardRequest("reactions_trend", date_range.start_date, date_range.end_date),
    createCardRequest("comments_trend", date_range.start_date, date_range.end_date),
    createCardRequest("reshares_trend", date_range.start_date, date_range.end_date),
    createCardRequest("members_reached_trend", date_range.start_date, date_range.end_date)
  ];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

// Flexible multi-card request
export const createGetMultipleV2CardsRequest = (linkedin_account_id, cards, date_range, page_id = null) => {
  const cards_request = cards.map(cardType =>
    createCardRequest(cardType, date_range.start_date, date_range.end_date)
  );
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

// Specific request for followers and reshares trends (as per your example)
export const createGetFollowersAndResharesV2Request = (linkedin_account_id, date_range, page_id = null) => {
  const cards_request = [
    createCardRequest("followers_trend", date_range.start_date, date_range.end_date),
    createCardRequest("reshares_trend", date_range.start_date, date_range.end_date)
  ];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

// V2 API compatible equivalents of V1 functions
export const createGetAnalyticsCardsV2Request = (linkedin_account_id, date_range, page_id = null) => {
  const cards_request = [
    createCardRequest("followers_trend", date_range.start_date, date_range.end_date),
    createCardRequest("impressions_trend", date_range.start_date, date_range.end_date),
    createCardRequest("reactions_trend", date_range.start_date, date_range.end_date),
    createCardRequest("comments_trend", date_range.start_date, date_range.end_date),
    createCardRequest("reshares_trend", date_range.start_date, date_range.end_date),
    { type: "posts_performance", params: { start_date: date_range.start_date, end_date: date_range.end_date, limit: 100 } },
  ];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

export const createGetProfileTrendsV2Request = (linkedin_account_id, date_range, page_id = null) => {
  // Equivalent to V1's createGetProfileTrendsRequest
  const cards_request = [createCardRequest("followers_trend", date_range.start_date, date_range.end_date)];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

export const createGetEngagementTrendsV2Request = (linkedin_account_id, date_range, page_id = null) => {
  // Equivalent to V1's createEngagementTrendsRequest
  const cards_request = [
    createCardRequest("impressions_trend", date_range.start_date, date_range.end_date),
    createCardRequest("reactions_trend", date_range.start_date, date_range.end_date),
    createCardRequest("comments_trend", date_range.start_date, date_range.end_date),
    createCardRequest("reshares_trend", date_range.start_date, date_range.end_date)
  ];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

// Client analytics for dashboard
export const createGetClientAnalyticsCardsV2Request = (linkedin_account_id, date_range, page_id = null) => {
  // Equivalent to V1's createGetClientAnalyticsCardsRequest
  const cards_request = [
    createCardRequest("followers_trend", date_range.start_date, date_range.end_date),
    createCardRequest("impressions_trend", date_range.start_date, date_range.end_date)
  ];
  return createV2BaseRequest(linkedin_account_id, cards_request, page_id);
};

// Helper function to check if response has errors
export const hasV2ApiErrors = (response) => {
  if (!response?.cards) return false;
  return response.cards.some(card => !card.success);
};

// Helper function to get failed cards from response
export const getFailedV2Cards = (response) => {
  if (!response?.cards) return [];
  return response.cards.filter(card => !card.success);
};

// Helper function to check for token-related errors
export const hasTokenErrors = (response) => {
  const failedCards = getFailedV2Cards(response);
  return failedCards.some(card =>
    card.error?.code === 'INVALID_TOKEN' ||
    card.error?.code === 'EXPIRED_TOKEN'
  );
};

// Helper function to get successfully loaded cards
export const getSuccessfulV2Cards = (response) => {
  if (!response?.cards) return [];
  return response.cards.filter(card => card.success);
};

// V2 API management functions - using existing LinkedIn integrations API
// LinkedIn account management is handled by LinkedInIntegrationsRequestFactory.
// The legacy V1 analytics-profile shims (createGetConnectedProfile,
// createConnectProfileRequest, createCheckConnectProfileAllowedRequest)
// were removed alongside SUP-389 — weekly content reports are now
// workspace-scoped and nothing on the FE creates / lists / deletes
// per-profile analytics settings anymore.
